import { APIService } from '@apparel/core/api/APIService';
import { ApplicationInterface } from '@lib/classes/API.interface';
import { http } from '@lib/http';
import { ModelClass } from '@model/Model';
import { ModelEndpoint, ModelEndpointValue } from '@model/utils/Model.interface';
import { AxiosPromise, AxiosRequestConfig } from 'axios';
import { size, toLower } from 'lodash';

interface PrivateAPI {
    _configFactory(config: AxiosRequestConfig, params: any): AxiosRequestConfig;
}

interface APIRequestConfig extends AxiosRequestConfig {
    tree?: boolean;
}

/**
 * API Factory
 * @param ModelDefinition
 * @param endpointURL
 * @author: Dumitrana Alinus <alin.designstudio@gmail.com>
 * @date: 08.10.2018
 * @time: 10:50
 * @constructor
 */
export const ApplicationInterfaceFactory = <T = ModelClass>(
    ModelDefinition: T & ApplicationInterface & PrivateAPI,
    endpointURL: ModelEndpoint = '',
): T & ApplicationInterface => {
    // noinspection JSUnusedGlobalSymbols
    ModelDefinition.prototype.http$ = http;
    ModelDefinition.http$ = http;
    /**
     * Endpoint helper
     * @param prop
     * @param method
     * @return {*}
     */
    const endpoint = (prop: string, method = 'get'): ModelEndpointValue => {
        if (typeof endpointURL === 'object') {
            return endpointURL[prop] as ModelEndpointValue;
        }

        return {
            url: endpointURL,
            method,
        };
    };

    /**
     * Request config factory
     * @param config
     * @param params
     * @private
     * Customer.index({
     *     return_only: 'id, name'
     * })
     */
    ModelDefinition._configFactory = (config: AxiosRequestConfig, params: any = {}): AxiosRequestConfig => {
        if (!config) {
            config = {};
        }

        params = {
            ...(params || {}),
            ...config.params,
        };

        params.return_only = params.fields || params.return_only;

        // noinspection JSUnresolvedVariable
        if (typeof ModelDefinition.$fields === 'function' && !params.return_only) {
            // noinspection JSUnresolvedFunction
            params.return_only = ModelDefinition.$fields();
        }

        return {
            ...config,
            params,
        };
    };

    /**
     * Index request (GET)
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.index = <T = any>(
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ): AxiosPromise<T> => {
        const endpoint$ = endpoint('index', 'get');

        return ModelDefinition.http$[endpoint$.method](endpoint$.url, ModelDefinition._configFactory(config, params));
    };

    /**
     * Index request (GET)
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.all = <T = any>(
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ): AxiosPromise<T> => {
        const endpoint$ = endpoint('all', 'get');
        params.all = true;

        return ModelDefinition.http$[endpoint$.method](endpoint$.url, ModelDefinition._configFactory(config, params));
    };

    /**
     * Find request (GET)
     * @param id
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.find = <T = any>(
        id: number,
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ): AxiosPromise<T> => {
        const endpoint$ = endpoint('find', 'get');

        return ModelDefinition.http$[endpoint$.method](
            `${endpoint$.url}/${id}`,
            ModelDefinition._configFactory(config, params),
        );
    };

    /**
     * Create request (POST)
     * @param data
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.create = (
        data: { [key: string]: any } = {},
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ) => {
        const endpoint$ = endpoint('create', 'post');

        return ModelDefinition.http$[endpoint$.method](
            endpoint$.url,
            data,
            ModelDefinition._configFactory(config, params),
        );
    };

    /**
     * Update request (PUT)
     * @param id
     * @param data
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.update = (
        id: number,
        data: { [key: string]: any } = {},
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ) => {
        const endpoint$ = endpoint('update', 'put');

        if (data && typeof data.$changes === 'function') {
            data = data.$changes();
        }

        let method = endpoint$.method;
        const config$ = ModelDefinition._configFactory(config, params);

        if (data instanceof FormData && toLower(method) === 'put') {
            method = 'post';
            config$.params._method = endpoint$.method;
        }

        return ModelDefinition.http$[method](`${endpoint$.url}${id !== null ? `/${id}` : ''}`, data, config$);
    };

    /**
     * Destroy request (PUT)
     * @param id
     * @param params
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.destroy = (id: number, params: { [key: string]: any } = {}, config: AxiosRequestConfig = {}) => {
        const endpoint$ = endpoint('destroy', 'delete');

        return ModelDefinition.http$[endpoint$.method](
            `${endpoint$.url}/${id}`,
            ModelDefinition._configFactory(config, params),
        );
    };

    ModelDefinition.request = <T = any>(config: AxiosRequestConfig = {}): AxiosPromise<T> => {
        const endpoint$ = endpoint(config.method ?? 'index', config.method);
        return APIService.createEndpoint(`${endpoint$.url}`.replace('//', '')).request(
            ModelDefinition._configFactory({ ...config }, config.params),
        );
    };

    /**
     * Create a tree request
     * @param method
     * @returns {function(config: AxiosRequestConfig): Promise}
     */
    function createTreeRequest(method) {
        return function (this: ApplicationInterface, config: APIRequestConfig = {}) {
            const executeTree = config.tree || typeof config.tree === 'undefined';
            const changes = this.$changes(!executeTree);
            const requests: Promise<any>[] = [];
            let requestData: { [key: string]: any } = {};

            if (executeTree) {
                Object.entries(changes).forEach(([key, value]: any[]) => {
                    // Send a tree request just if the value it's a valid model instance
                    if (value instanceof ModelClass && size(value.$changes()) > 0) {
                        requests.push(
                            new Promise((resolve) => {
                                const value$: any = value;
                                const req = value$.id > 0 ? value$.$update(config) : value$.$save(config);

                                const resolver = (response) => {
                                    // In case of id response we will set to the request data
                                    if (response.data && response.data.id) {
                                        requestData[`${key}_id`] = response.data.id;
                                    }

                                    // In case of request error pass changes to root request tree
                                    if (response.response && response.response.status !== 200) {
                                        requestData[key] = value.$changes();
                                    }

                                    resolve({ response, config, value, key });
                                };

                                req.then(resolver, resolver).catch(resolver);
                            }),
                        );
                    } else {
                        requestData[key] = value;
                    }
                });
            } else {
                requestData = { ...changes };
            }

            return new Promise((resolve, reject) => {
                const resolver = (results) => {
                    const argv = [requestData, {}, config];

                    if (method === 'update') {
                        argv.unshift(requestData.id || this.id);
                    }

                    // After all tree request we will send our root request
                    ModelDefinition[method](...argv).then(({ data }) => {
                        const model = this.$fill(data);

                        // Filling it with all given data
                        results.forEach(({ value, key, response }) => {
                            const { data } = response;
                            model.set(key, value.$fill(data));
                        });

                        resolve(model);
                    }, reject);
                };

                if (executeTree) {
                    Promise.all(requests).then(resolver, reject).catch(reject);
                } else {
                    resolver([]);
                }
            });
        };
    }

    // noinspection JSUnusedGlobalSymbols
    /**
     * Save request alias of Create
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.prototype.$save = createTreeRequest('create');

    /**
     * Synchronize request alias of Update
     * @param config
     * @return {AxiosPromise<any>}
     */
    ModelDefinition.prototype.$sync = createTreeRequest('update');

    // noinspection JSUnusedGlobalSymbols
    /**
     * Update request
     * @type {function(config: AxiosRequestConfig): Promise}
     */
    ModelDefinition.prototype.$update = ModelDefinition.prototype.$sync;

    /**
     * Destroy the item
     * @param params
     * @param config
     */
    ModelDefinition.prototype.$destroy = function (
        params: { [key: string]: any } = {},
        config: AxiosRequestConfig = {},
    ) {
        return ModelDefinition.destroy(this.id, params, config);
    };

    return ModelDefinition;
};
